<template>
  <BaseDialog centered contentClass="add-competitor" ref="dialog" persistent :show-actions="true">
    <template slot="title">{{ $t('page.business-intelligence.competitor.add-dialog.title') }}</template>
    <template slot="content">
      <form @click.prevent @keydown.enter.prevent>
        <FormInput
          ref="competitorName"
          :label="$t('page.business-intelligence.competitor.add-dialog.name')"
          is-required
          has-gray-background
          name="name"
          type="name"
          v-model="competitorName"
          v-validate="'required'"
          @input="competitorNameInput"
          :error="competitorNameError"
        />
        <div v-if="competitorName.length > 0">
          <div class="add-competitor__label">
            <FormLabel is-required>{{
              $t('page.business-intelligence.competitor.add-dialog.normalized-name')
            }}</FormLabel>
            <BaseIcon
              v-tooltip="{ content: $t('page.business-intelligence.competitor.alias-tooltip'), placement: 'top' }"
              name="info"
              class="add-competitor__label-info"
              role="img"
              tabindex="0"
              :aria-label="$t('page.business-intelligence.competitor.alias-tooltip')"
            />
          </div>
          <NameSuggestion
            ref="nameSuggestionRef"
            :suggestions="suggestions"
            :loading="pending"
            :existingSelections="addedNormalizedNames"
            @updated-selected-names="updateSelectedNames"
            @on-search="refreshSuggestions"
            @all-names-selected="updateSuggestions"
          />
        </div>
        <div class="add-competitor__actions">
          <FormButton is-outlined class="add-competitor__cancel" @click.native="cancelClick">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton @click.native="onSubmit" type="submit" :disabled="isFormInvalid">
            <span>{{ $t('actions.save') }}</span>
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DebounceMixin from '@/mixins/debounce-mixin';
import NameSuggestion from '@/views/business-intelligence/NameSuggestion.vue';
import BaseDialog from '@/components/base/BaseDialog';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import FormLabel from '@/components/form/FormLabel.vue';
import { normalizeStr } from '@/util/string-util';

export default {
  name: 'AddCompetitorDialog',
  mixins: [DebounceMixin],
  components: {
    BaseDialog,
    FormButton,
    FormInput,
    NameSuggestion,
    FormLabel,
    BaseIcon,
  },
  data() {
    return {
      competitorName: '',
      addedNormalizedNames: [],
      suggestions: [],
      showCustomInput: false,
    };
  },
  computed: {
    ...mapGetters('biDashboard', ['pending', 'competitors']),
    isFormInvalid() {
      return this.addedNormalizedNames.length === 0 || this.competitorNameError !== undefined;
    },
    currentCompetitor() {
      return this.competitors ? this.competitors.map((c) => c.name) : [];
    },
    competitorNameError() {
      for (const competitor of this.competitors) {
        if (this.competitorName && this.competitorName.trim().toLowerCase() === competitor.name.trim().toLowerCase()) {
          return this.$t('error.code.DUPLICATED_COMPETITOR');
        }
      }
      return undefined;
    },
  },
  methods: {
    ...mapActions('biDashboard', ['getNormalizedNameSuggestion', 'cancelGetNormalizedNameSuggestion', 'addCompetitor']),
    open(name = '') {
      this.competitorName = name;
      this.addedNormalizedNames = [];
      this.suggestions = [];
      this.defaultSuggestions = [];

      this.$refs.dialog.openModal();

      this.$nextTick(() => {
        const refInput = this.$refs.competitorName.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });

      if (name && name !== '') {
        this.updateSelectedNames([normalizeStr(name, ' ')]);
        this.getNormalizedNameSuggestion(name).then((response) => {
          if (response) {
            this.defaultSuggestions = response;
            this.suggestions = response;
          }
        });
      }
    },
    close() {
      // Not shown if user has not input a competitor name
      if (this.$refs.nameSuggestionRef) {
        this.$refs.nameSuggestionRef.reset();
      }
      this.$refs.dialog.isModalOpen = false;
    },
    competitorNameInput(event) {
      this.debounce(() => {
        if (this.competitorNameError) {
          this.cancelGetNormalizedNameSuggestion();
        } else {
          this.getNormalizedNameSuggestion(event).then((response) => {
            if (response) {
              this.defaultSuggestions = response;
              this.suggestions = response;
            }
          });
        }
      }, 1000);
    },
    refreshSuggestions(input) {
      if (input.length > 0) {
        this.getNormalizedNameSuggestion(input).then((response) => (this.suggestions = response));
      } else {
        this.suggestions = this.defaultSuggestions;
      }
    },
    updateSelectedNames(names) {
      this.addedNormalizedNames = names;
    },
    updateSuggestions() {
      this.suggestions = this.defaultSuggestions;
    },
    onSubmit() {
      this.addCompetitor({ name: this.competitorName.trim(), normalizedNames: this.addedNormalizedNames })
        .then(() => {
          this.$toast.success(this.$tc('page.business-intelligence.competitor.add-dialog.add-success'));
          this.$emit('action-succeeded');
        })
        .catch((error) => this.$toast.error(error));
      this.close();
    },
    cancelClick() {
      this.close();
    },
  },
};
</script>

<style lang="scss">
.base-dialog__add-competitor {
  height: auto;
  width: 550px;
  position: relative;

  .base-dialog__content {
    padding: 8px 32px 32px 32px !important;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}
.add-competitor {
  &__label {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__label-info {
    width: 14px;
    height: 14px;
    margin-bottom: 6px;
    flex-shrink: 0;
    color: $color-normal-pale;
    cursor: help;

    &:focus-visible {
      outline: 2px solid $color-accent;
      outline-offset: 2px;
      border-radius: 50%;
    }
  }

  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: 12px;
  }

  &__action {
    text-transform: lowercase;
    font-weight: bold;
  }
}
</style>
